import { useCallback, useEffect, useRef, useState } from "react";
import { getClosedStakes, getOwned, getStaked } from "./supernovaChain";

/**
 * useSupernovaChain — the Supernova tab's read-only view of the member's wallet.
 *
 * Three independent slices, each with its OWN status, because they fail
 * independently and mean different things:
 *
 *   owned  — ERC-1155 balances (real chain read)
 *   staked — open stake lots (real chain read: getUserStakes + stakes, see
 *            supernovaStakeRead)
 *   closed — closed stake lots, rebuilt from the contract's event log (see
 *            supernovaClosedLots). The banked half of XP comes from here, and
 *            it is deliberately SEPARATE from `staked`: it is a log scan rather
 *            than a storage read, so it fails on its own terms. A failed closed
 *            read must leave the accruing figure intact and simply say the
 *            lifetime total isn't complete.
 *
 * Status vocabulary:
 *   "idle"        nothing requested (flag off, or no wallet connected)
 *   "loading"     read in flight
 *   "ready"       we have data — `owned` / `staked` is trustworthy
 *   "error"       the read failed; show the error, NOT a zero
 *   "unavailable" the feature isn't wired up yet; show a placeholder, NOT a zero
 *
 * The distinction is the whole point: `owned === null` with status "error"
 * must never render as "you own 0 NFTs". Data and the absence of data are
 * different states in this hook and must stay different in the UI.
 *
 * FLAG OFF ⇒ NOTHING HAPPENS. With `enabled` false (its default) the hook makes
 * no client, no RPC call, and no network request of any kind. Callers pass the
 * SUPERNOVA_TAB_ENABLED gate in.
 */
export default function useSupernovaChain(wallet, options = {}) {
  const { enabled = false, client } = options;
  const active = Boolean(enabled && wallet);

  const [owned, setOwned] = useState(null);
  const [ownedStatus, setOwnedStatus] = useState("idle");
  const [ownedError, setOwnedError] = useState(null);

  const [staked, setStaked] = useState(null);
  const [stakedStatus, setStakedStatus] = useState("idle");
  const [stakedError, setStakedError] = useState(null);

  const [closed, setClosed] = useState(null);
  const [closedStatus, setClosedStatus] = useState("idle");
  const [closedError, setClosedError] = useState(null);

  /* Guards against a resolved read from a previous wallet (or an unmounted
     component) overwriting the current one. */
  const requestRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!active) return;
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    const current = () => requestRef.current === requestId;

    setOwnedStatus("loading");
    setStakedStatus("loading");
    setClosedStatus("loading");

    const [ownedResult, stakedResult, closedResult] = await Promise.allSettled([
      getOwned(wallet, { client }),
      getStaked(wallet, { client }),
      getClosedStakes(wallet, { client }),
    ]);

    if (!current()) return;

    if (ownedResult.status === "fulfilled") {
      setOwned(ownedResult.value);
      setOwnedError(null);
      setOwnedStatus("ready");
    } else {
      // Keep no stale data around: a failed read leaves nothing to render.
      setOwned(null);
      setOwnedError(ownedResult.reason);
      setOwnedStatus("error");
    }

    if (stakedResult.status === "fulfilled") {
      setStaked(stakedResult.value);
      setStakedError(null);
      setStakedStatus("ready");
    } else {
      setStaked(null);
      setStakedError(stakedResult.reason);
      setStakedStatus(stakedResult.reason?.unavailable ? "unavailable" : "error");
    }

    if (closedResult.status === "fulfilled") {
      setClosed(closedResult.value);
      setClosedError(null);
      setClosedStatus("ready");
    } else {
      setClosed(null);
      setClosedError(closedResult.reason);
      setClosedStatus(closedResult.reason?.unavailable ? "unavailable" : "error");
    }
  }, [active, wallet, client]);

  useEffect(() => {
    if (!active) {
      // Disconnecting the wallet (or flipping the flag off) clears everything
      // and invalidates any read still in flight.
      requestRef.current += 1;
      setOwned(null);
      setOwnedError(null);
      setOwnedStatus("idle");
      setStaked(null);
      setStakedError(null);
      setStakedStatus("idle");
      setClosed(null);
      setClosedError(null);
      setClosedStatus("idle");
      return;
    }
    refresh();
  }, [active, refresh]);

  return {
    owned,
    ownedStatus,
    ownedError,
    staked,
    stakedStatus,
    stakedError,
    closed,
    closedStatus,
    closedError,
    loading: ownedStatus === "loading" || stakedStatus === "loading",
    refresh,
  };
}
